import {
  Autocomplete,
  Drawer,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  TextFieldProps,
} from '@mui/material';
import CustomButton from '../../../../Components/CustomButton';
import { useEffect, useRef, useState } from 'react';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import '../../../../styles/filter.scss';
import { useDispatch } from 'react-redux';
import { setFilterApplied } from '../../../../redux/commonSlice';
import { getDepartmentList } from '../../../../redux/ProductionSlice';
import Images from '../../../../Utils/images';
import { useAppSelector } from '../../../../redux/store';

interface FilterProps {
  filter?: Record<string, unknown>;
  setFilter?: (value: any) => void;
  productionId?: string | null;
}

const SORT_BY_OPTIONS = [
  { id: 1, label: 'Default', value: 'default' },
  { id: 2, label: 'Member Assigned', value: 'memberAssigned' },
  { id: 3, label: 'Member Not Assigned', value: 'memberNotAssigned' },
];

export default function Filter({
  setFilter,
  productionId,
  filter: _filter,
}: FilterProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);
  const [selectedSortBy, setSelectedSortBy] = useState('');
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const [tempSelectedDepartments, setTempSelectedDepartments] = useState<any[]>(
    [],
  );
  const [tempSelectedSortBy, setTempSelectedSortBy] = useState('');

  const dispatch: any = useDispatch();
  const { departmentlist } = useAppSelector((state: any) => state.production);
  const lastFetchedProductionIdRef = useRef<string | null>(null);

  const handleOpenFilter = (): void => {
    if (productionId && lastFetchedProductionIdRef.current !== productionId) {
      lastFetchedProductionIdRef.current = productionId;
      getDepartmentList(productionId, 'productions');
    }
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      setTempSelectedDepartments([...selectedDepartments]);
      setTempSelectedSortBy(selectedSortBy);
    }
  }, [open, selectedDepartments, selectedSortBy]);

  const handleReset = (): void => {
    setSelectedDepartments([]);
    setSelectedSortBy('');
    setIsFilterApplied(false);
    dispatch(setFilterApplied(false));
    setFilter?.((prev: any) => ({
      ...prev,
      page: 1,
      sortBy: undefined,
      departmentIds: undefined,
    }));
    setTimeout(() => setOpen(false), 300);
  };

  const handleApply = (): void => {
    setSelectedDepartments(tempSelectedDepartments);
    setSelectedSortBy(tempSelectedSortBy);
    setIsFilterApplied(true);
    dispatch(setFilterApplied(true));

    const departmentIds: string[] = tempSelectedDepartments
      .map((dept: any) => dept._id)
      .filter(Boolean);

    setFilter?.((prev: any) => ({
      ...prev,
      page: 1,
      sortBy: tempSelectedSortBy || undefined,
      departmentIds: departmentIds.length > 0 ? departmentIds : undefined,
    }));

    setTimeout(() => setOpen(false), 300);
  };

  return (
    <div className="reelFilter">
      <CustomButton
        onClick={handleOpenFilter}
        size="large"
        variant="outlined"
        text="Filter"
        showIcon={true}
        width="auto"
        type="button"
        id="filter"
        loading={false}
        iconPosition="start"
        icon={Images.FILTER}
        className={isFilterApplied ? 'ifFilterApplied lg-height' : 'lg-height'}
      />

      <Drawer
        anchor="right"
        open={open}
        onClose={(): void => setOpen(false)}
        transitionDuration={500}
      >
        <div className="filterWrapper">
          <div className="filterHead">
            <h3>Filter by:</h3>
            <IconButton onClick={(): void => setOpen(false)}>
              <HighlightOffOutlinedIcon />
            </IconButton>
          </div>

          <div className="filterBody">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div className="form-in lg">
                  <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                    Sort By
                  </FormLabel>
                  <Autocomplete
                    disableCloseOnSelect
                    options={SORT_BY_OPTIONS}
                    getOptionLabel={(option: any) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    value={
                      SORT_BY_OPTIONS.find(
                        item => item.value === tempSelectedSortBy,
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setTempSelectedSortBy(newValue?.value || '');
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} className="filters-dropdown">
                        <div className="radio">
                          <input type="radio" checked={selected} readOnly />
                          <label>
                            <span>{option.label}</span>
                          </label>
                        </div>
                      </li>
                    )}
                    renderInput={(params: TextFieldProps): JSX.Element => (
                      <TextField
                        {...params}
                        placeholder={tempSelectedSortBy ? '' : 'Select Sort By'}
                        variant="outlined"
                      />
                    )}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="form-in lg">
                  <FormLabel className="MuiTypography-subtitle1 text-black fw400 mb-8 d-flex">
                    Select Department
                  </FormLabel>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={departmentlist?.depts ?? departmentlist ?? []}
                    getOptionLabel={(option: any) => option.deptName ?? ''}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    value={tempSelectedDepartments}
                    onChange={(_, newValue) => {
                      setTempSelectedDepartments(newValue);
                    }}
                    renderOption={(props, option, { selected: _selected }) => (
                      <li {...props} className="filters-dropdown">
                        <div className="checkbox">
                          <input
                            type="checkbox"
                            checked={tempSelectedDepartments.some(
                              dept => dept._id === option._id,
                            )}
                            readOnly
                          />
                          <label>
                            <span>{option.deptName}</span>
                          </label>
                        </div>
                      </li>
                    )}
                    renderInput={(params: TextFieldProps): JSX.Element => (
                      <TextField
                        {...params}
                        placeholder={
                          tempSelectedDepartments.length > 0
                            ? ''
                            : 'Select Departments'
                        }
                        variant="outlined"
                      />
                    )}
                  />
                </div>
              </Grid>
            </Grid>
          </div>

          <div className="filterFooter">
            <CustomButton
              onClick={handleReset}
              size="large"
              variant="outlined"
              text="Reset"
              width="100%"
              type="button"
              id="reset"
              showIcon={false}
            />
            <CustomButton
              onClick={handleApply}
              size="large"
              variant="contained"
              text="Apply"
              width="100%"
              type="button"
              id="apply"
              showIcon={false}
            />
          </div>
        </div>
      </Drawer>
    </div>
  );
}
